// src/components/DashboardUsuario.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import gatoIcon from "../assets/gato.png";
import perroIcon from "../assets/perro.png";
import roedorIcon from "../assets/roedor.png";
import aveIcon from "../assets/ave.png";
import otroIcon from "../assets/otro.png";

/*
  LocalStorage esperado (con fallback si no existen):
  - usuarioActual: { id, nombre, email }
  - mascotas: [{ id, nombre, tipo("Felino"|"Canino"|"Roedor"|"Ave"|"Otro"), foto(base64)?, propietarioEmail }]
  - citas: [{ id, propietarioEmail?, propietario?, mascotaId?, mascota?, tipo, fecha(YYYY-MM-DD), hora(HH:MM), cancelada? }]
  - historialMedico: [{ id, mascotaId, fecha, resumen }]
  - clinica: { telefono, email }
*/

function safeParse(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

export default function DashboardUsuario({ onLogout, onAgendar, onAgendarCita }) {
  // permite ambas props para agendar
  const handleAgendar = onAgendar || onAgendarCita;

  // ===== Usuario actual =====
  const usuarioActual =
    safeParse("usuarioActual", { nombre: "Usuario", email: "demo@user.com" });

  // ===== Mascotas (con CRUD y fotos) =====
  const [mascotas, setMascotas] = useState(() => {
    const m = safeParse("mascotas", []);
    if (Array.isArray(m) && m.length) return m;
    const correo = usuarioActual?.email || "demo@user.com";
    return [
      { id: "m1", nombre: "Luna", tipo: "Felino", propietarioEmail: correo },
      { id: "m2", nombre: "Max",  tipo: "Canino", propietarioEmail: correo },
    ];
  });

  // ===== Citas / Historial / Clínica =====
  const [citas, setCitas] = useState(() => {
    const c = safeParse("citas", []);
    if (Array.isArray(c) && c.length) return c;
    const correo = usuarioActual?.email || "demo@user.com";
    return [
      { id: "c1", propietarioEmail: correo, mascotaId: "m1", tipo: "Vacuna",          fecha: "2025-08-20", hora: "13:00" },
      { id: "c2", propietarioEmail: correo, mascotaId: "m2", tipo: "Control",         fecha: "2025-08-21", hora: "11:00" },
      { id: "c3", propietarioEmail: correo, mascotaId: "m1", tipo: "Chequeo",         fecha: "2025-08-22", hora: "10:00" },
    ];
  });

  const [historial] = useState(() => {
    const h = safeParse("historialMedico", []);
    if (Array.isArray(h) && h.length) return h;
    return [
      { id: "h1", mascotaId: "m1", fecha: "2025-07-30", resumen: "Vacuna antirrábica aplicada." },
      { id: "h2", mascotaId: "m2", fecha: "2025-07-18", resumen: "Control general. Todo estable." },
    ];
  });

  const [clinica] = useState(() =>
    safeParse("clinica", { telefono: "000 223 450", email: "contacto@mascoticas.com" })
  );

  // ===== Reloj / responsive =====
  const [ahora, setAhora] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setAhora(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const [isNarrow, setIsNarrow] = useState(() => window.innerWidth <= 900);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ===== Modal Historial =====
  const [showHistory, setShowHistory] = useState(false);
  const [filtroMascota, setFiltroMascota] = useState("todas");

  // ===== Modal Mascotas (crear/editar) =====
  const [showPetModal, setShowPetModal] = useState(false);
  const [petForm, setPetForm] = useState({ id: null, nombre: "", tipo: "Felino", foto: null });

  // ===== Derivados =====
  const correoUsuario = (usuarioActual?.email || "").toLowerCase();
  const nombreUsuario = (usuarioActual?.nombre || "").toLowerCase();

  const misMascotas = useMemo(() => {
    return (mascotas || []).filter((m) => {
      const owner = (m.propietarioEmail || "").toLowerCase();
      return owner ? owner === correoUsuario : true;
    });
  }, [mascotas, correoUsuario]);

  const parseDT = (c) => new Date(`${c.fecha}T${(c.hora || "00:00")}:00`);

  const matchCitaUsuario = useCallback(
    (c) => {
      const porEmail = (c.propietarioEmail || "").toLowerCase() === correoUsuario;
      const porNombre = (c.propietario || "").toLowerCase() === nombreUsuario;
      return porEmail || porNombre;
    },
    [correoUsuario, nombreUsuario]
  );

  const misCitas = useMemo(() => {
    const hoy = new Date(ahora.toDateString());
    return (citas || [])
      .filter((c) => matchCitaUsuario(c) && !c.cancelada && parseDT(c) >= hoy)
      .sort((a, b) => parseDT(a) - parseDT(b));
  }, [citas, matchCitaUsuario, ahora]);

  const proximasCitas = misCitas.slice(0, 4);

  const misRegistros = useMemo(() => {
    const ids = new Set(misMascotas.map((m) => m.id));
    return (historial || []).filter((h) => ids.has(h.mascotaId));
  }, [historial, misMascotas]);

  // ===== Utilidades =====
  const iconoPorTipo = (t) => {
    switch (t) {
      case "Felino": return gatoIcon;
      case "Canino": return perroIcon;
      case "Roedor": return roedorIcon;
      case "Ave":    return aveIcon;
      default:       return otroIcon;
    }
  };

  const nombreMascota = (c) => {
    if (c.mascota) return c.mascota;
    const m = misMascotas.find((mm) => mm.id === c.mascotaId);
    return m ? m.nombre : "Mascota";
  };

  const hhmm = (d) => d.toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit", hour12: true });

  const cancelarCita = (id) => {
    if (!window.confirm("¿Deseas cancelar esta cita?")) return;
    const next = citas.map((c) => (c.id === id ? { ...c, cancelada: true } : c));
    setCitas(next);
    localStorage.setItem("citas", JSON.stringify(next));
  };

  // ===== CRUD Mascotas =====
  const abrirNuevaMascota = () => {
    setPetForm({ id: null, nombre: "", tipo: "Felino", foto: null });
    setShowPetModal(true);
  };

  const abrirEditarMascota = (m) => {
    setPetForm({
      id: m.id,
      nombre: m.nombre || "",
      tipo: m.tipo || "Felino",
      foto: m.foto || null,
    });
    setShowPetModal(true);
  };

  const handleFotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setPetForm((f) => ({ ...f, foto: reader.result }));
    reader.readAsDataURL(file);
  };

  const quitarFoto = () => setPetForm((f) => ({ ...f, foto: null }));

  const guardarMascota = (e) => {
    e.preventDefault();
    const nombre = petForm.nombre.trim();
    if (!nombre) {
      alert("El nombre es obligatorio");
      return;
    }

    const base = {
      nombre,
      tipo: petForm.tipo,
      foto: petForm.foto || null,
      propietarioEmail: usuarioActual?.email || "",
    };

    let nuevas;
    if (petForm.id) {
      nuevas = mascotas.map((m) => (m.id === petForm.id ? { ...m, ...base } : m));
    } else {
      const id = "m" + Date.now();
      nuevas = [...mascotas, { id, ...base }];
    }

    setMascotas(nuevas);
    localStorage.setItem("mascotas", JSON.stringify(nuevas));
    setShowPetModal(false);
  };

  const eliminarMascota = (id) => {
    if (!window.confirm("¿Eliminar esta mascota?")) return;
    const nuevas = mascotas.filter((m) => m.id !== id);
    setMascotas(nuevas);
    localStorage.setItem("mascotas", JSON.stringify(nuevas));
  };

  // ===== Estilos =====
  const st = {
    page: { minHeight: "100vh", background: "linear-gradient(180deg,#f5f9ff 0%, #ffffff 100%)", padding: 24, fontFamily: "system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif" },
    header: { display: "flex", alignItems: "center", justifyContent: "space-between" },
    title: { margin: 0, fontSize: 28, letterSpacing: 1, color: "#0f172a" },
    actions: { display: "flex", gap: 10 },
    btn: { border: "none", borderRadius: 10, padding: "8px 12px", cursor: "pointer", background: "#e2e8f0", color: "#0f172a", fontWeight: 600 },
    primary: { background: "#3b82f6", color: "#fff" },
    danger: { background: "#ef4444", color: "#fff" },
    outline: { background: "#fff", border: "1px solid #cbd5e1" },
    sub: { display: "flex", alignItems: "center", justifyContent: "space-between", margin: "10px 0 20px", color: "#334155" },
    clock: { fontWeight: 700, color: "#0284c7" },
    grid: { display: "grid", gridTemplateColumns: isNarrow ? "1fr" : "repeat(12,1fr)", gap: 16 },
    card: { background: "#fff", borderRadius: 16, padding: 16, boxShadow: "0 6px 24px rgba(15,23,42,.06)", border: "1px solid #edf2f7", gridColumn: isNarrow ? "span 1" : "span 6" },
    headRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 },
    h2: { margin: 0, color: "#0f172a" },
    // mascotas
    pets: { display: "flex", flexWrap: "wrap", gap: 12 },
    pet: { textAlign: "center", width: 140, padding: 10, borderRadius: 12, border: "1px solid #e5e7eb", background: "#fafafa" },
    avatar: { width: 90, height: 90, borderRadius: "50%", objectFit: "cover", display: "block", margin: "0 auto 8px", border: "2px solid #e5e7eb" },
    petName: { fontSize: 14, marginTop: 4, color: "#334155", fontWeight: 700 },
    chip: { padding: "2px 8px", borderRadius: 999, fontSize: 11, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", display: "inline-block", marginTop: 4 },
    smRow: { display: "flex", gap: 6, justifyContent: "center", marginTop: 8 },
    smBtn: { border: "1px solid #cbd5e1", background: "#fff", borderRadius: 8, padding: "4px 8px", fontSize: 12, cursor: "pointer" },
    smDanger: { border: "1px solid #fecaca", background: "#fff5f5", color: "#b91c1c" },
    // citas
    col: { display: "flex", flexDirection: "column", gap: 10 },
    appt: { display: "grid", gridTemplateColumns: "1fr auto auto", alignItems: "center", gap: 12, padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: 12 },
    apptTitle: { fontWeight: 700, color: "#111827" },
    apptSub: { color: "#64748b", fontSize: 13 },
    apptTime: { color: "#0284c7", fontWeight: 700 },
    pill: { padding: "6px 10px", borderRadius: 999, border: "none", cursor: "pointer", background: "#fde68a", color: "#92400e", fontWeight: 600 },
    empty: { color: "#64748b" },
    // contacto
    phone: { marginBottom: 12, fontSize: 18, fontWeight: 800, color: "#0f172a" },
    // modal
    mbg: { position: "fixed", inset: 0, background: "rgba(15,23,42,.35)", display: "grid", placeItems: "center", zIndex: 50 },
    modal: { background: "#fff", borderRadius: 16, padding: 16, width: "min(520px,92vw)", boxShadow: "0 20px 50px rgba(0,0,0,.2)" },
    mhead: { display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #e2e8f0", paddingBottom: 8, marginBottom: 12 },
    icon: { background: "transparent", border: "none", fontSize: 18, cursor: "pointer" },
    formRow: { display: "grid", gap: 10, marginBottom: 12 },
    label: { fontSize: 13, color: "#334155", marginBottom: 4 },
    input: { padding: "10px 12px", borderRadius: 10, border: "1px solid #cbd5e1", outline: "none" },
    select: { padding: "10px 12px", borderRadius: 10, border: "1px solid #cbd5e1", background: "#fff" },
    preview: { width: 90, height: 90, borderRadius: "50%", objectFit: "cover", border: "2px solid #e5e7eb", display: "block", margin: "8px auto" },
    actionsEnd: { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 },
  };

  // ===== Render =====
  const previewSrc = petForm.foto || iconoPorTipo(petForm.tipo);

  return (
    <div style={st.page}>
      {/* Header */}
      <div style={st.header}>
        <h1 style={st.title}>MASCOTICAS SENA</h1>
        <div style={st.actions}>
          {handleAgendar && <button style={{ ...st.btn, ...st.primary }} onClick={handleAgendar}>Agendar Cita</button>}
          {onLogout && <button style={{ ...st.btn, ...st.danger }} onClick={onLogout}>Cerrar sesión</button>}
        </div>
      </div>

      <div style={st.sub}>
        <span>👋 Hola, {usuarioActual?.nombre || "Usuario"}</span>
        <span style={st.clock}>🕒 {hhmm(ahora)}</span>
      </div>

      {/* GRID 2x2 */}
      <div style={st.grid}>
        {/* Mis Mascotas */}
        <section style={st.card}>
          <div style={st.headRow}>
            <h2 style={st.h2}>Mis Mascotas</h2>
            <button style={{ ...st.btn, ...st.outline }} onClick={abrirNuevaMascota}>+ Agregar Mascota</button>
          </div>
          <div style={st.pets}>
            {misMascotas.map((m) => (
              <div style={st.pet} key={m.id} title={m.nombre}>
                <img
                  src={m.foto || iconoPorTipo(m.tipo)}
                  alt={m.nombre}
                  style={st.avatar}
                />
                <div style={st.petName}>{m.nombre}</div>
                <div style={st.chip}>{m.tipo}</div>
                <div style={st.smRow}>
                  <button style={st.smBtn} onClick={() => abrirEditarMascota(m)}>Editar</button>
                  <button style={{ ...st.smBtn, ...st.smDanger }} onClick={() => eliminarMascota(m.id)}>Eliminar</button>
                </div>
              </div>
            ))}
            {!misMascotas.length && <div style={st.empty}>Aún no tienes mascotas registradas.</div>}
          </div>
        </section>

        {/* Próximas Citas */}
        <section style={st.card}>
          <h2 style={st.h2}>Próximas Citas</h2>
          <div style={st.col}>
            {proximasCitas.length ? (
              proximasCitas.map((c) => (
                <div key={c.id} style={st.appt}>
                  <div>
                    <div style={st.apptTitle}>{nombreMascota(c)} — {c.tipo}</div>
                    <div style={st.apptSub}>{c.fecha}</div>
                  </div>
                  <div style={st.apptTime}>{c.hora}</div>
                  <button style={st.pill} onClick={() => cancelarCita(c.id)}>Cancelar</button>
                </div>
              ))
            ) : (
              <div style={st.empty}>No tienes próximas citas.</div>
            )}
          </div>
        </section>

        {/* Historial Médico */}
        <section style={st.card}>
          <h2 style={st.h2}>Historial Médico</h2>
          <p style={{ color: "#475569" }}>
            {misRegistros.length
              ? `Recientes: ${misRegistros.slice(0, 2).map((h) => {
                  const m = misMascotas.find((mm) => mm.id === h.mascotaId);
                  return `${m?.nombre || "Mascota"} (${h.fecha})`;
                }).join(" · ")}`
              : "Aún no hay registros clínicos."}
          </p>
          <button style={{ ...st.btn, ...st.outline }} onClick={() => setShowHistory(true)}>
            Ver historial completo
          </button>
        </section>

        {/* Contactar Clínica */}
        <section style={st.card}>
          <h2 style={st.h2}>Contactar Clínica</h2>
          <div style={st.phone}>📞 {clinica.telefono || "000 223 450"}</div>
          <button
            style={{ ...st.btn, ...st.primary }}
            onClick={() => window.open(`mailto:${clinica.email || "contacto@mascoticas.com"}`)}
          >
            Enviar mensaje
          </button>
        </section>
      </div>

      {/* Modal Historial */}
      {showHistory && (
        <div style={st.mbg} onClick={() => setShowHistory(false)}>
          <div style={st.modal} onClick={(e) => e.stopPropagation()}>
            <div style={st.mhead}>
              <h3 style={{ margin: 0 }}>Historial Médico</h3>
              <button style={st.icon} onClick={() => setShowHistory(false)}>✖</button>
            </div>

            <div style={{ display: "flex", gap: 10, marginBottom: 12, alignItems: "center" }}>
              <select
                value={filtroMascota}
                onChange={(e) => setFiltroMascota(e.target.value)}
                style={st.select}
              >
                <option value="todas">Todas mis mascotas</option>
                {misMascotas.map((m) => (
                  <option key={m.id} value={m.id}>{m.nombre}</option>
                ))}
              </select>
              <button style={st.btn} onClick={() => setFiltroMascota("todas")}>Limpiar filtro</button>
            </div>

            <div style={{ overflow: "auto", maxHeight: "60vh" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ padding: 10, borderBottom: "1px solid #e2e8f0", textAlign: "left", background: "#f8fafc" }}>Mascota</th>
                    <th style={{ padding: 10, borderBottom: "1px solid #e2e8f0", textAlign: "left", background: "#f8fafc" }}>Especie</th>
                    <th style={{ padding: 10, borderBottom: "1px solid #e2e8f0", textAlign: "left", background: "#f8fafc" }}>Fecha</th>
                    <th style={{ padding: 10, borderBottom: "1px solid #e2e8f0", textAlign: "left", background: "#f8fafc" }}>Resumen</th>
                  </tr>
                </thead>
                <tbody>
                  {misRegistros
                    .filter((r) => filtroMascota === "todas" || r.mascotaId === filtroMascota)
                    .map((r) => {
                      const m = misMascotas.find((mm) => mm.id === r.mascotaId);
                      return (
                        <tr key={r.id}>
                          <td style={{ padding: 10, borderBottom: "1px solid #e2e8f0" }}>{m?.nombre || "—"}</td>
                          <td style={{ padding: 10, borderBottom: "1px solid #e2e8f0" }}>{m ? m.tipo : "—"}</td>
                          <td style={{ padding: 10, borderBottom: "1px solid #e2e8f0" }}>{r.fecha}</td>
                          <td style={{ padding: 10, borderBottom: "1px solid #e2e8f0" }}>{r.resumen}</td>
                        </tr>
                      );
                    })}
                  {!misRegistros.length && (
                    <tr><td style={{ padding: 10 }} colSpan={4}>Sin registros.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <button style={st.btn} onClick={() => setShowHistory(false)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Crear/Editar Mascota */}
      {showPetModal && (
        <div style={st.mbg} onClick={() => setShowPetModal(false)}>
          <div style={st.modal} onClick={(e) => e.stopPropagation()}>
            <div style={st.mhead}>
              <h3 style={{ margin: 0 }}>{petForm.id ? "Editar Mascota" : "Agregar Mascota"}</h3>
              <button style={st.icon} onClick={() => setShowPetModal(false)}>✖</button>
            </div>

            <form onSubmit={guardarMascota}>
              <div style={st.formRow}>
                <label style={st.label}>Nombre</label>
                <input
                  type="text"
                  value={petForm.nombre}
                  onChange={(e) => setPetForm({ ...petForm, nombre: e.target.value })}
                  style={st.input}
                  placeholder="Nombre de la mascota"
                />
              </div>

              <div style={st.formRow}>
                <label style={st.label}>Tipo de animal</label>
                <select
                  value={petForm.tipo}
                  onChange={(e) => setPetForm({ ...petForm, tipo: e.target.value })}
                  style={st.select}
                >
                  <option>Felino</option>
                  <option>Canino</option>
                  <option>Roedor</option>
                  <option>Ave</option>
                  <option>Otro</option>
                </select>
              </div>

              <div style={st.formRow}>
                <label style={st.label}>Foto</label>
                <input type="file" accept="image/*" onChange={handleFotoChange} />
                <img src={previewSrc} alt="Previsualización" style={st.preview} />
                {petForm.foto && (
                  <button type="button" onClick={quitarFoto} style={st.smBtn}>
                    Quitar foto
                  </button>
                )}
              </div>

              <div style={st.actionsEnd}>
                <button type="button" style={st.btn} onClick={() => setShowPetModal(false)}>
                  Cancelar
                </button>
                <button type="submit" style={{ ...st.btn, ...st.primary }}>
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
